import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Clapperboard, LayoutTemplate as LayoutTemplateIcon, Search, Plus, Clock,
  Pencil, Copy, Trash2, Link2, Music, Sparkles, Loader2, FlaskConical,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Badge } from '@meno/ui/components/ui/badge'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@meno/ui/components/ui/dialog'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import { TextInput } from '@meno/ui/components/shared/TextInput'
import { TabButton } from '@meno/ui/components/shared/TabButton'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import {
  getPlaylists, createPlaylist, updatePlaylist, deletePlaylist, publishPlaylist,
  getMedia, getAudios, getAudioPlaylists,
  getSignageLayouts, createSignageLayout, updateSignageLayout, deleteSignageLayout, publishSignageLayout,
  getMixes, createMix, deleteMix, type Mix,
} from '@meno/ui/lib/api'
import EmptyState from '@/components/EmptyState'
import PlaylistDialog, { type PlaylistInput, type PlaylistMediaFile, type PlaylistLinkItem, type EventFields } from '@/components/PlaylistDialog'
import { PublishDialog } from '@/components/PublishDialog'
import LayoutDialog from '@/components/LayoutDialog'
import MixDialog, { type MixDialogSave } from '@/components/MixDialog'
import { ContentCard, ContentListRow, type CardMenuItem } from '@/components/ContentCard'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import ViewToggle from '@/components/ViewToggle'
import CompanionSection from '@/components/CompanionSection'
import { companionsToPayload, companionsFromEntity, type Companions, type CompanionFields } from '@/components/companionsModel'
import { withCompanions } from '@/lib/companions'
import { TRANSITIONS } from '@/lib/transitions'
import { loadLinks as loadInteractiveLinks } from '@/lib/interactiveLinks'
import { playlistThumbUrl, layoutThumbUrl } from '@/lib/playlistThumb'
import {
  getTemplate, type LayoutTemplate, buildEmptyZones,
  gridTracks, zoneStyle,
} from '@/lib/layoutTemplates'

// Content Hub mirrors the Asset page's tab pattern — flush tab strip
// under TopBar, then search + action row, view-toggle row, then the
// content grid. Playlists tab is wired to the real /playlists endpoint;
// the other tabs are still placeholder empty-states.
type TabKey = 'playlists' | 'layouts' | 'links'

// A "lab" is the companion bundle (audio + stop screen + transition) carried
// on a playlist or layout. The Labs tab surfaces these as one editable list,
// backed by the existing companion_* columns — no separate entity.
type LinkRow = {
  kind: 'playlist' | 'layout'
  id: number
  name: string
  companions: CompanionFields
}

type PlaylistRow = PlaylistInput & {
  total_duration: number
  screens: { id: number; name: string; branch?: { id: number; name: string } | null }[]
  created_at: string
}

type LayoutRow = {
  id: number
  name: string
  description?: string
  template: LayoutTemplate
  zones: Array<{ id: string; name: string; media_ids: number[]; link_ids: number[] }>
  column_weights?: number[] | null
  row_weights?: number[] | null
  created_at: string
} & CompanionFields

export default function ContentHubPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canCreate = can('playlists:create')
  usePageHeader({ title: t('nav.contentHub', 'Collections') })

  const [active, setActive] = useState<TabKey>('playlists')
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')

  // Playlists state — fetched from /playlists. Loaded once on mount and
  // refreshed when the create dialog closes after a successful save.
  const [playlists, setPlaylists] = useState<PlaylistRow[]>([])
  const [plLoading, setPlLoading] = useState(true)
  const [allMedia, setAllMedia] = useState<PlaylistMediaFile[]>([])
  const [allLinks, setAllLinks] = useState<PlaylistLinkItem[]>([])
  const [createOpen, setCreateOpen] = useState(false)

  // Card-action dialogs — share one set of state across all cards so we
  // don't render N dialogs at once.
  const [sendTarget, setSendTarget]     = useState<PlaylistRow | null>(null)
  const [renameTarget, setRenameTarget] = useState<PlaylistRow | null>(null)
  const [renameDraft, setRenameDraft]   = useState('')
  const [deleteTarget, setDeleteTarget] = useState<PlaylistRow | null>(null)

  // Layouts state + card-action dialogs.
  const [layouts, setLayouts]         = useState<LayoutRow[]>([])
  const [layoutsLoading, setLayoutsLoading] = useState(true)
  const [layoutCreateOpen, setLayoutCreateOpen] = useState(false)
  const [layoutSendTarget, setLayoutSendTarget]     = useState<LayoutRow | null>(null)
  const [layoutRenameTarget, setLayoutRenameTarget] = useState<LayoutRow | null>(null)
  const [layoutRenameDraft, setLayoutRenameDraft]   = useState('')
  const [layoutDeleteTarget, setLayoutDeleteTarget] = useState<LayoutRow | null>(null)

  // Labs tab — editing a playlist/layout's publish-together companions.
  // Catalogs resolve the linked audio's name for the row summaries.
  const [audios, setAudios] = useState<{ id: number; original_name: string }[]>([])
  const [audioPlaylists, setAudioPlaylists] = useState<{ id: number; name: string }[]>([])
  const [linkEdit, setLinkEdit] = useState<LinkRow | null>(null)
  const [linkDraft, setLinkDraft] = useState<Companions>(companionsFromEntity(null))
  const [linkSaving, setLinkSaving] = useState(false)

  // Lab tab now lists standalone Mixes (the companion bundle promoted to an
  // entity). Each mix opens its own detail page for editing + publish/schedule.
  const [mixes, setMixes] = useState<Mix[]>([])
  const [mixLoading, setMixLoading] = useState(true)
  const [mixDeleteTarget, setMixDeleteTarget] = useState<Mix | null>(null)
  const [mixCreateOpen, setMixCreateOpen] = useState(false)
  const [mixCreating, setMixCreating] = useState(false)
  const loadMixes = useCallback(() => {
    setMixLoading(true)
    getMixes().then(r => setMixes(r.data)).catch(() => {}).finally(() => setMixLoading(false))
  }, [])

  const loadLayouts = useCallback(() => {
    setLayoutsLoading(true)
    getSignageLayouts()
      .then(r => setLayouts(r.data))
      .catch(() => {})
      .finally(() => setLayoutsLoading(false))
  }, [])

  const loadPlaylists = useCallback(() => {
    setPlLoading(true)
    getPlaylists()
      .then(r => setPlaylists(r.data))
      .catch(() => {})
      .finally(() => setPlLoading(false))
  }, [])

  useEffect(() => { loadPlaylists() }, [loadPlaylists])
  useEffect(() => { loadLayouts() },   [loadLayouts])
  useEffect(() => { loadMixes() },     [loadMixes])
  useEffect(() => { getMedia(false, 'signage').then(r => setAllMedia(r.data)).catch(() => {}) }, [])
  useEffect(() => { loadInteractiveLinks().then(setAllLinks).catch(() => {}) }, [])
  useEffect(() => { getAudios().then(r => setAudios(r.data || [])).catch(() => {}) }, [])
  useEffect(() => { getAudioPlaylists().then(r => setAudioPlaylists(r.data || [])).catch(() => {}) }, [])

  // Open the link editor for a playlist/layout, seeded from its companions.
  const openLinkEdit = (row: LinkRow) => {
    setLinkDraft(companionsFromEntity(row.companions))
    setLinkEdit(row)
  }

  // Persist the edited companions via PATCH (reuses the update endpoints — no
  // new entity) and reflect the change in the local list.
  const saveLink = async () => {
    if (!linkEdit) return
    setLinkSaving(true)
    try {
      const payload = companionsToPayload(linkDraft)
      if (linkEdit.kind === 'playlist') {
        const { data } = await updatePlaylist(linkEdit.id, payload)
        setPlaylists(prev => prev.map(p => p.id === linkEdit.id ? { ...p, ...data } : p))
      } else {
        const { data } = await updateSignageLayout(linkEdit.id, payload)
        setLayouts(prev => prev.map(l => l.id === linkEdit.id ? { ...l, ...data } : l))
      }
      toast.success(t('companions.linkUpdated', 'Lab updated'))
      setLinkEdit(null)
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setLinkSaving(false)
    }
  }

  // Human summary of a companion bundle for the Links list rows.
  const linkSummary = (c: CompanionFields): { audio: string | null; transition: string | null } => {
    const audio = c.companion_audio_id != null
      ? audios.find(a => a.id === c.companion_audio_id)?.original_name ?? t('companions.audio', 'Audio')
      : c.companion_audio_playlist_id != null
        ? audioPlaylists.find(p => p.id === c.companion_audio_playlist_id)?.name ?? t('companions.audioPlaylist', 'Audio playlist')
        : null
    const transition = c.companion_transition
      ? TRANSITIONS.find(tr => tr.key === c.companion_transition)?.name ?? c.companion_transition
      : null
    return { audio, transition }
  }

  const tabs = [
    {
      key: 'playlists' as const,
      label: t('contentHub.tabs.playlists', 'Playlists'),
      icon: Clapperboard,
      emptyTitle: t('contentHub.playlistsEmptyTitle', 'No playlists yet'),
      emptyDescription: t('contentHub.playlistsEmpty', 'Playlists stitch media and layouts together into a timed list that screens can loop.'),
      searchPlaceholder: t('contentHub.searchPlaylists', 'Search Playlists…'),
      newButtonLabel: t('contentHub.newPlaylist', 'New Playlist'),
    },
    {
      key: 'layouts' as const,
      label: t('contentHub.tabs.layouts', 'Layouts'),
      icon: LayoutTemplateIcon,
      emptyTitle: t('contentHub.layoutsEmptyTitle', 'No layouts yet'),
      emptyDescription: t('contentHub.layoutsEmpty', 'Layouts define how regions split a screen — the templates your playlists pour content into.'),
      searchPlaceholder: t('contentHub.searchLayouts', 'Search Layouts…'),
      newButtonLabel: t('contentHub.newLayout', 'New Layout'),
    },
    {
      key: 'links' as const,
      label: t('contentHub.tabs.links', 'Lab'),
      icon: FlaskConical,
      emptyTitle: t('contentHub.linksEmptyTitle', 'No mixes yet'),
      emptyDescription: t('contentHub.linksEmpty', 'A mix bundles a playlist or layout with an audio source, a stop screen, and a transition into one named set you can preview and publish or schedule to screens.'),
      searchPlaceholder: t('contentHub.searchLinks', 'Search Mixes…'),
      newButtonLabel: t('contentHub.newMix', 'New Mix'),
    },
  ]
  const tab = tabs.find(tb => tb.key === active)!

  const onNewClick = () => {
    if (active === 'playlists') setCreateOpen(true)
    else if (active === 'layouts') setLayoutCreateOpen(true)
    else if (active === 'links') setMixCreateOpen(true)
  }

  // Create a mix with every ingredient the operator picked in the dialog
  // (content/audio/stop screen/transition), then jump to its detail page —
  // same "pick everything up front" flow Playlist/Layout creation use, not
  // just a name with ingredients deferred to the next page. Guards
  // re-entrancy so a double-click on the dialog's Create button can't
  // double-POST.
  const handleCreateMix = async (data: MixDialogSave) => {
    if (mixCreating) return
    setMixCreating(true)
    try {
      const { data: mix } = await createMix({
        name: data.name,
        playlist_id: data.playlistId,
        layout_id: data.layoutId,
        audio_id: data.audioId,
        audio_playlist_id: data.audioPlaylistId,
        stop_screen_id: data.stopScreenId,
        transition: data.transition,
      })
      setMixCreateOpen(false)
      navigate(`/management/mixes/${mix.id}`)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.error', 'Something went wrong'))
      throw err
    } finally {
      setMixCreating(false)
    }
  }

  const handleDeleteMix = async () => {
    if (!mixDeleteTarget) return
    try {
      await deleteMix(mixDeleteTarget.id)
      setMixes(prev => prev.filter(m => m.id !== mixDeleteTarget.id))
      setMixDeleteTarget(null)
      toast.success(t('mix.deleted', 'Mix deleted'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.error', 'Something went wrong'))
    }
  }

  // Playlist create — reuses the same PlaylistDialog used by the Asset
  // page. On success we navigate straight into the new playlist's detail
  // page so the user can add media, schedule, etc.
  const handleCreatePlaylist = async (
    name: string, description: string,
    mediaIds: number[], mediaDurations: Record<number, number>,
    linkIds: number[], linkDurations: Record<number, number>,
    mediaOrder: Record<number, number>, linkOrder: Record<number, number>,
    event: EventFields,
    companions: Companions,
  ): Promise<void> => {
    try {
      const { data } = await createPlaylist({
        name,
        description: description || undefined,
        media_ids: mediaIds,
        media_durations: mediaDurations,
        link_ids: linkIds,
        link_durations: linkDurations,
        media_order: mediaOrder,
        link_order: linkOrder,
        ...event,
        ...companionsToPayload(companions),
      })
      setCreateOpen(false)
      toast.success(t('contentHub.playlistCreated', 'Playlist created'))
      navigate(`/management/media/playlists/${data.id}`)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('contentHub.playlistCreateFailed', 'Failed to create playlist'))
      throw err
    }
  }

  // ── Card actions ──────────────────────────────────────────────────────
  // Rename / duplicate / delete operate on whichever PlaylistRow was set
  // by the card's 3-dot menu. Publish opens the same stubbed dialog as the
  // detail page so behaviour is identical regardless of entry point.
  const openRename = (row: PlaylistRow) => {
    setRenameTarget(row)
    setRenameDraft(row.name)
  }
  const commitRename = async () => {
    if (!renameTarget) return
    const next = renameDraft.trim()
    if (!next || next === renameTarget.name) { setRenameTarget(null); return }
    try {
      const { data } = await updatePlaylist(renameTarget.id, { name: next })
      setPlaylists(prev => prev.map(p => p.id === data.id ? data : p))
      toast.success(t('common.renamedSuccess', 'Renamed successfully'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setRenameTarget(null)
    }
  }

  // Duplicate works by POSTing a new playlist that carries the same
  // media / link selection plus order / duration maps. Name gets a
  // " (copy)" suffix so the original is easy to tell apart in the grid.
  const duplicatePlaylist = async (row: PlaylistRow) => {
    try {
      const mediaIds = row.media.map(m => m.id)
      const linkIds  = (row.links ?? []).map(l => l.id)
      const { data } = await createPlaylist({
        name: `${row.name} (copy)`,
        description: row.description || undefined,
        media_ids: mediaIds,
        media_durations: row.media_durations,
        link_ids: linkIds,
        link_durations: row.link_durations ?? {},
        media_order: row.media_order ?? {},
        link_order:  row.link_order  ?? {},
        is_event: row.is_event ?? false,
        event_start_date: row.event_start_date ?? null,
        event_end_date:   row.event_end_date   ?? null,
        event_start_time: row.event_start_time ?? null,
        event_end_time:   row.event_end_time   ?? null,
        companion_audio_id:          row.companion_audio_id          ?? null,
        companion_audio_playlist_id: row.companion_audio_playlist_id ?? null,
        companion_stop_screen_id:    row.companion_stop_screen_id    ?? null,
        companion_transition:        row.companion_transition        ?? null,
      })
      setPlaylists(prev => [data, ...prev])
      toast.success(t('playlist.duplicated', 'Playlist duplicated'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deletePlaylist(deleteTarget.id)
      setPlaylists(prev => prev.filter(p => p.id !== deleteTarget.id))
      toast.success(t('common.deleted', 'Deleted'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setDeleteTarget(null)
    }
  }

  const filteredPlaylists = playlists.filter(s =>
    !search.trim() || s.name.toLowerCase().includes(search.toLowerCase()),
  )

  // ── Layout handlers ───────────────────────────────────────────────────
  // Create flow: POST → on success, prepend to the local list and navigate
  // to the new layout's detail page. Previously this called navigate()
  // without seeding the list, and the detail page bounced back to the hub
  // when its catalogue fetches stumbled — which read to the operator as
  // "the layout didn't get created." On failure we re-throw so the dialog's
  // own catch can surface the reason inline (in addition to a toast).
  const handleCreateLayout = async (data: { name: string; description: string; template: LayoutTemplate; zones?: ReturnType<typeof buildEmptyZones>; companions: Companions }) => {
    try {
      const { data: created } = await createSignageLayout({
        name: data.name,
        description: data.description || undefined,
        template: data.template,
        zones: data.zones ?? buildEmptyZones(data.template),
        ...companionsToPayload(data.companions),
      })
      setLayouts(prev => [created, ...prev])
      setLayoutCreateOpen(false)
      toast.success(t('layout.created', 'Layout created'))
      navigate(`/management/layouts/${created.id}`)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('layout.createFailed', 'Failed to create layout'))
      throw err
    }
  }

  const openLayoutRename = (row: LayoutRow) => {
    setLayoutRenameTarget(row)
    setLayoutRenameDraft(row.name)
  }
  const commitLayoutRename = async () => {
    if (!layoutRenameTarget) return
    const next = layoutRenameDraft.trim()
    if (!next || next === layoutRenameTarget.name) { setLayoutRenameTarget(null); return }
    try {
      const { data } = await updateSignageLayout(layoutRenameTarget.id, { name: next })
      setLayouts(prev => prev.map(p => p.id === data.id ? data : p))
      toast.success(t('common.renamedSuccess', 'Renamed successfully'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setLayoutRenameTarget(null)
    }
  }
  const duplicateLayout = async (row: LayoutRow) => {
    try {
      const { data } = await createSignageLayout({
        name: `${row.name} (copy)`,
        description: row.description || undefined,
        template: row.template,
        zones: row.zones,
        companion_audio_id:          row.companion_audio_id          ?? null,
        companion_audio_playlist_id: row.companion_audio_playlist_id ?? null,
        companion_stop_screen_id:    row.companion_stop_screen_id    ?? null,
        companion_transition:        row.companion_transition        ?? null,
      })
      setLayouts(prev => [data, ...prev])
      toast.success(t('layout.duplicated', 'Layout duplicated'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    }
  }
  const confirmLayoutDelete = async () => {
    if (!layoutDeleteTarget) return
    try {
      await deleteSignageLayout(layoutDeleteTarget.id)
      setLayouts(prev => prev.filter(p => p.id !== layoutDeleteTarget.id))
      toast.success(t('common.deleted', 'Deleted'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setLayoutDeleteTarget(null)
    }
  }

  const filteredLayouts = layouts.filter(l =>
    !search.trim() || l.name.toLowerCase().includes(search.toLowerCase()),
  )

  // Editing a link changes the entity's companions — same capability as any
  // other playlist/layout edit.
  const canEditLinks = can('playlists:edit')

  // Merged, searchable list of every playlist + layout as a link row, so their
  // publish-together companions can be managed in one place.
  const linkRows: LinkRow[] = [
    ...playlists.map((p): LinkRow => ({ kind: 'playlist', id: p.id, name: p.name, companions: p })),
    ...layouts.map((l): LinkRow => ({ kind: 'layout', id: l.id, name: l.name, companions: l })),
  ]
  const filteredLinkRows = linkRows.filter(r =>
    !search.trim() || r.name.toLowerCase().includes(search.toLowerCase()),
  )
  const filteredMixes = mixes.filter(m =>
    !search.trim() || m.name.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    // Bounded to the shell panel so the empty state can fill the leftover
    // height (flex-1) instead of sitting as a short card near the top; the
    // grids scroll inside their own region while the tabs/toolbar stay pinned.
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      {/* Tab strip — flush under TopBar via same -mx/-mt trick the Asset
          page uses, so this strip lines up identically. */}
      <div className="-mx-4 sm:-mx-[var(--space-l)] -mt-1 sm:-mt-[calc(var(--space-l)-0.75rem)] mb-0 px-4 sm:px-[var(--space-l)] border-b border-border bg-transparent shrink-0">
        <div className="flex gap-1">
          {tabs.map(tb => (
            <TabButton
              key={tb.key}
              active={active === tb.key}
              onClick={() => setActive(tb.key)}
              icon={tb.icon}
              label={tb.label}
            />
          ))}
        </div>
      </div>

      {/* Search + view toggle + New action — all on one row. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={tab.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ps-9 h-9 transition-colors focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:text-foreground"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ViewToggle mode={viewMode} onChange={setViewMode} />
          {canCreate && tab.newButtonLabel && (
            <Button
              size="sm"
              className="gap-2 h-9 shrink-0"
              onClick={onNewClick}
              disabled={active === 'links' && mixCreating}
            >
              <Plus className="w-4 h-4" />
              {tab.newButtonLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Tab body — fills remaining height; grids scroll, empty state centers. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      {active === 'playlists' ? (
        plLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-video w-full rounded-xl" />
            ))}
          </div>
        ) : filteredPlaylists.length === 0 ? (
          <EmptyState icon={tab.icon} title={tab.emptyTitle} description={tab.emptyDescription} />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredPlaylists.map(seq => (
              <PlaylistCard
                key={seq.id}
                seq={seq}
                onOpen={() => navigate(`/management/media/playlists/${seq.id}`)}
                onSend={() => setSendTarget(seq)}
                onRename={() => openRename(seq)}
                onDuplicate={() => duplicatePlaylist(seq)}
                onDelete={() => setDeleteTarget(seq)}
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {filteredPlaylists.map(seq => (
              <PlaylistListRow
                key={seq.id}
                seq={seq}
                onOpen={() => navigate(`/management/media/playlists/${seq.id}`)}
                onSend={() => setSendTarget(seq)}
                onRename={() => openRename(seq)}
                onDuplicate={() => duplicatePlaylist(seq)}
                onDelete={() => setDeleteTarget(seq)}
              />
            ))}
          </ul>
        )
      ) : active === 'layouts' ? (
        layoutsLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-video w-full rounded-xl" />
            ))}
          </div>
        ) : filteredLayouts.length === 0 ? (
          <EmptyState icon={tab.icon} title={tab.emptyTitle} description={tab.emptyDescription} />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredLayouts.map(lay => (
              <LayoutCard
                key={lay.id}
                layout={lay}
                thumb={layoutThumbUrl(lay.zones, allMedia)}
                onOpen={() => navigate(`/management/layouts/${lay.id}`)}
                onSend={() => setLayoutSendTarget(lay)}
                onRename={() => openLayoutRename(lay)}
                onDuplicate={() => duplicateLayout(lay)}
                onDelete={() => setLayoutDeleteTarget(lay)}
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {filteredLayouts.map(lay => (
              <LayoutListRow
                key={lay.id}
                layout={lay}
                thumb={layoutThumbUrl(lay.zones, allMedia)}
                onOpen={() => navigate(`/management/layouts/${lay.id}`)}
                onSend={() => setLayoutSendTarget(lay)}
                onRename={() => openLayoutRename(lay)}
                onDuplicate={() => duplicateLayout(lay)}
                onDelete={() => setLayoutDeleteTarget(lay)}
              />
            ))}
          </ul>
        )
      ) : (
        /* ── Lab — standalone Mixes (the companion bundle promoted to an
           entity). Each opens its own detail page to edit + publish/schedule. ─ */
        mixLoading ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-video w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <ul className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </ul>
          )
        ) : filteredMixes.length === 0 ? (
          <EmptyState icon={tab.icon} title={tab.emptyTitle} description={tab.emptyDescription} />
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredMixes.map(m => (
              <MixCard
                key={m.id}
                mix={m}
                onOpen={() => navigate(`/management/mixes/${m.id}`)}
                onDelete={canEditLinks ? () => setMixDeleteTarget(m) : undefined}
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {filteredMixes.map(m => (
              <MixListRow
                key={m.id}
                mix={m}
                onOpen={() => navigate(`/management/mixes/${m.id}`)}
                onDelete={canEditLinks ? () => setMixDeleteTarget(m) : undefined}
              />
            ))}
          </ul>
        )
      )}
      </div>

      {/* Create-playlist dialog — the same PlaylistDialog the Asset page
          uses, so the form / picker / event mode all behave the same here. */}
      {createOpen && (
        <PlaylistDialog
          playlist={null}
          allMedia={allMedia}
          allLinks={allLinks}
          thumbCache={{}}
          onSave={handleCreatePlaylist}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {/* Publish dialog — the same screen/group picker the playlist detail page
          uses, wired to the real /playlists/{id}/publish endpoint. On success we
          reload so each card's "N screens" subtitle reflects the new assignment. */}
      <PublishDialog
        open={!!sendTarget}
        kind="playlist"
        publish={withCompanions(payload => publishPlaylist(sendTarget!.id, payload))}
        onClose={() => setSendTarget(null)}
        onPublished={() => { setSendTarget(null); loadPlaylists() }}
      />

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('common.rename', 'Rename')}</DialogTitle>
          </DialogHeader>
          <TextInput
            label={t('common.playlistName', 'Playlist name')}
            value={renameDraft}
            onChange={setRenameDraft}
            maxLength={150}
            lang="any"
            autoFocus
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRenameTarget(null)}>{t('common.cancel')}</Button>
            <Button disabled={!renameDraft.trim()} onClick={commitRename}>{t('common.save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm — when the playlist is currently published to screens,
          warn that they go blank: the screens' channel entries are removed with
          the playlist (ondelete=CASCADE) and the delete endpoint nudges those
          players, so they clear straight away until something else is published. */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('common.deletePlaylist', 'Delete Playlist')}
        description={t('common.deletePlaylistDesc', 'This playlist will be removed. Media files are not deleted.')}
        warning={
          deleteTarget && deleteTarget.screens.length > 0
            ? t(
                'common.deletePlaylistDescPublished',
                "Showing on these screens — they'll go blank until you publish something else:",
              )
            : undefined
        }
        warningItems={
          deleteTarget && deleteTarget.screens.length > 0
            ? deleteTarget.screens.map(s =>
                `${s.name} — ${s.branch?.name ?? t('common.deletePlaylistScreenNoBranch', 'No branch')}`,
              )
            : undefined
        }
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* ── Lab: delete a mix ────────────────────────────────────── */}
      <ConfirmDialog
        open={!!mixDeleteTarget}
        title={t('mix.deleteTitle', 'Delete mix')}
        description={t('mix.deleteDesc', 'This mix will be removed. The audio, stop screen and transition it referenced are not deleted.')}
        onConfirm={handleDeleteMix}
        onClose={() => setMixDeleteTarget(null)}
      />

      {/* ── Lab: create a mix — same "name it, land on the detail page" flow
          as Playlist/Layout creation, instead of a blind create+navigate. ── */}
      {mixCreateOpen && (
        <MixDialog
          onSave={handleCreateMix}
          onClose={() => setMixCreateOpen(false)}
        />
      )}

      {/* ── Layouts: dialogs ─────────────────────────────────────── */}
      {layoutCreateOpen && (
        <LayoutDialog
          layout={null}
          onSave={handleCreateLayout}
          onClose={() => setLayoutCreateOpen(false)}
        />
      )}

      {/* Publish dialog — same picker as playlists, wired to the real
          /signage-layouts/{id}/publish endpoint. */}
      <PublishDialog
        open={!!layoutSendTarget}
        mode="publish"
        kind="layout"
        publish={withCompanions(payload => publishSignageLayout(layoutSendTarget!.id, payload))}
        onClose={() => setLayoutSendTarget(null)}
        onPublished={() => { setLayoutSendTarget(null); loadLayouts() }}
      />

      <Dialog open={!!layoutRenameTarget} onOpenChange={(o) => { if (!o) setLayoutRenameTarget(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('common.rename', 'Rename')}</DialogTitle>
          </DialogHeader>
          <TextInput
            label={t('layout.name', 'Layout name')}
            value={layoutRenameDraft}
            onChange={setLayoutRenameDraft}
            maxLength={150}
            lang="any"
            autoFocus
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLayoutRenameTarget(null)}>{t('common.cancel')}</Button>
            <Button disabled={!layoutRenameDraft.trim()} onClick={commitLayoutRename}>{t('common.save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!layoutDeleteTarget}
        title={t('layout.deleteTitle', 'Delete Layout')}
        description={t('layout.deleteDesc', 'This layout will be removed. The media and links it references are not deleted.')}
        onConfirm={confirmLayoutDelete}
        onClose={() => setLayoutDeleteTarget(null)}
      />

      {/* ── Labs: edit a playlist/layout's publish-together companions ─── */}
      <Dialog open={!!linkEdit} onOpenChange={o => { if (!o && !linkSaving) setLinkEdit(null) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('companions.editLink', 'Edit lab')}
              {linkEdit ? ` — ${linkEdit.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">
            {t('companions.editLinkDesc', 'The lab that publishes together with this item.')}
          </p>
          <CompanionSection value={linkDraft} onChange={setLinkDraft} />
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={linkSaving} onClick={() => setLinkEdit(null)}>{t('common.cancel', 'Cancel')}</Button>
            <Button disabled={linkSaving} onClick={saveLink} className="gap-2">
              {linkSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Link list row ───────────────────────────────────────────────────────────
// One playlist/layout shown as its publish-together link: what audio +
// transition ride along when it's published. Editable in place.
function LinkListRow({
  row, summary, canEdit, onEdit,
}: {
  row: LinkRow
  summary: { audio: string | null; transition: string | null }
  canEdit: boolean
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const hasLink = !!summary.audio || !!summary.transition
  return (
    <li>
      <div
        role={canEdit ? 'button' : undefined}
        tabIndex={canEdit ? 0 : undefined}
        onClick={canEdit ? onEdit : undefined}
        onKeyDown={e => { if (canEdit && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onEdit() } }}
        className={`flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 ${canEdit ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}`}
      >
        <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center shrink-0">
          <Link2 className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">{row.name}</p>
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {row.kind === 'playlist' ? t('contentHub.tabs.playlists', 'Playlists') : t('contentHub.tabs.layouts', 'Layouts')}
            </Badge>
          </div>
          {hasLink ? (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
              {summary.audio && (
                <span className="inline-flex items-center gap-1"><Music className="w-3 h-3" />{summary.audio}</span>
              )}
              {summary.transition && (
                <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />{summary.transition}</span>
              )}
            </div>
          ) : (
            <p className="mt-0.5 text-[11px] text-muted-foreground">{t('companions.noLink', 'No lab')}</p>
          )}
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={e => { e.stopPropagation(); onEdit() }}>
            <Pencil className="w-3.5 h-3.5" />
            {hasLink ? t('common.edit', 'Edit') : t('companions.addLinkAction', 'Add')}
          </Button>
        )}
      </div>
    </li>
  )
}

// Human summary of a Mix's ingredients — main content, audio, stop screen,
// transition — shared by the grid card and the list row.
function mixSummary(mix: Mix, t: ReturnType<typeof useTranslation>['t']) {
  const parts: string[] = []
  const mainContent = mix.playlist_name ?? mix.layout_name
  if (mainContent) parts.push(mainContent)
  const audio = mix.audio_name ?? mix.audio_playlist_name
  if (audio) parts.push(audio)
  if (mix.stop_screen_name) parts.push(mix.stop_screen_name)
  if (mix.transition) parts.push(mix.transition)
  return parts.length ? parts.join(' · ') : t('mix.empty', 'No components yet')
}

function mixMenu(onDelete: (() => void) | undefined, t: ReturnType<typeof useTranslation>['t']): CardMenuItem[] {
  return onDelete ? [{ icon: Trash2, label: t('common.delete', 'Delete'), onClick: onDelete, destructive: true }] : []
}

// One Mix, as a grid tile — same shared ContentCard shell as playlists/layouts
// so the Lab tab gets the same card-or-list toggle everywhere else has.
function MixCard({ mix, onOpen, onDelete }: {
  mix: Mix; onOpen: () => void; onDelete?: () => void
}) {
  const { t } = useTranslation()
  return (
    <ContentCard
      title={mix.name}
      subtitle={mixSummary(mix, t)}
      onOpen={onOpen}
      menu={mixMenu(onDelete, t)}
      media={
        <div className="block w-full aspect-video bg-primary/10 overflow-hidden relative flex items-center justify-center">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center shadow-md">
            <FlaskConical className="w-7 h-7 text-primary-foreground" />
          </div>
        </div>
      }
    />
  )
}

// One Mix, as a list row — opens its detail page.
function MixListRow({ mix, onOpen, onDelete }: {
  mix: Mix; onOpen: () => void; onDelete?: () => void
}) {
  const { t } = useTranslation()
  return (
    <ContentListRow
      title={mix.name}
      subtitle={mixSummary(mix, t)}
      thumb={null}
      icon={FlaskConical}
      tintClassName="bg-primary/10"
      iconClassName="text-primary"
      onOpen={onOpen}
      menu={mixMenu(onDelete, t)}
    />
  )
}

// ── Playlist card / row ──────────────────────────────────────────────────
// Thin wrappers over the shared ContentCard/ContentListRow so playlists get
// the same footer contract as layouts and stop screens: an inline publish
// icon plus a 3-dot menu holding rename / duplicate / delete. Callbacks are
// owned by the parent so all the dialogs render once at the page root.
type PlaylistCardProps = {
  seq: PlaylistRow
  onOpen: () => void
  onSend: () => void
  onRename: () => void
  onDuplicate: () => void
  onDelete: () => void
}

// Content-authoring menu, gated by capability: rename/duplicate need
// playlists:edit, delete needs playlists:delete. Shared by playlists + layouts.
function useContentMenu({ onRename, onDuplicate, onDelete }: { onRename: () => void; onDuplicate: () => void; onDelete: () => void }): CardMenuItem[] {
  const { t } = useTranslation()
  const { can } = usePermissions()
  return [
    ...(can('playlists:edit') ? [
      { icon: Pencil, label: t('common.rename', 'Rename'),       onClick: onRename },
      { icon: Copy,   label: t('common.duplicate', 'Duplicate'), onClick: onDuplicate },
    ] : []),
    ...(can('playlists:delete') ? [
      { icon: Trash2, label: t('common.delete', 'Delete'),       onClick: onDelete, destructive: true },
    ] : []),
  ]
}
const usePlaylistMenu = useContentMenu

const fmtDuration = (sec: number) => {
  if (!sec) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function playlistSubtitle(seq: PlaylistRow, t: ReturnType<typeof useTranslation>['t']) {
  return seq.screens.length > 0
    ? t('contentHub.onScreens', '{{count}} screens', { count: seq.screens.length })
    : t('contentHub.notAssigned', 'Not assigned')
}

function PlaylistCard({ seq, onOpen, onSend, onRename, onDuplicate, onDelete }: PlaylistCardProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const menu = usePlaylistMenu({ onRename, onDuplicate, onDelete })
  const itemCount = (seq.media?.length ?? 0) + (seq.links?.length ?? 0)
  const thumb = playlistThumbUrl(seq.media)
  return (
    <ContentCard
      title={seq.name}
      subtitle={playlistSubtitle(seq, t)}
      onOpen={onOpen}
      onPublish={can('playlists:edit') ? onSend : undefined}
      menu={menu}
      media={
        <div className="block w-full aspect-video bg-teal-500/10 overflow-hidden relative">
          {thumb ? (
            <img src={thumb} alt={seq.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-14 h-14 rounded-2xl bg-teal-500 flex items-center justify-center shadow-md">
                <Clapperboard className="w-7 h-7 text-white" />
              </div>
            </div>
          )}
          {itemCount > 0 && (
            <span className="absolute top-2 start-2 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/50 text-white backdrop-blur-sm">
              <Clock className="w-2.5 h-2.5" /> {fmtDuration(seq.total_duration)}
            </span>
          )}
          {itemCount > 0 && (
            <Badge variant="secondary" className="absolute bottom-2 start-2 h-5 text-[10px] px-1.5">
              {itemCount} {t('common.items', 'items')}
            </Badge>
          )}
        </div>
      }
    />
  )
}

function PlaylistListRow({ seq, onOpen, onSend, onRename, onDuplicate, onDelete }: PlaylistCardProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const menu = usePlaylistMenu({ onRename, onDuplicate, onDelete })
  const itemCount = (seq.media?.length ?? 0) + (seq.links?.length ?? 0)
  const detail = itemCount > 0
    ? ` · ${itemCount} ${t('common.items', 'items')} · ${fmtDuration(seq.total_duration)}`
    : ''
  return (
    <ContentListRow
      title={seq.name}
      subtitle={`${playlistSubtitle(seq, t)}${detail}`}
      thumb={playlistThumbUrl(seq.media)}
      icon={Clapperboard}
      tintClassName="bg-teal-500/10"
      iconClassName="text-teal-600"
      onOpen={onOpen}
      onPublish={can('playlists:edit') ? onSend : undefined}
      menu={menu}
    />
  )
}

// ── Layout card / row ────────────────────────────────────────────────────
// Same shared-card contract as playlists. The grid tile shows the first
// renderable piece of zone content, falling back to the zone-arrangement
// mockup so the operator can still read the layout's shape at a glance.
type LayoutCardProps = {
  layout: LayoutRow
  thumb: string | null
  onOpen: () => void
  onSend: () => void
  onRename: () => void
  onDuplicate: () => void
  onDelete: () => void
}

const useLayoutMenu = useContentMenu

function LayoutCard({ layout, thumb, onOpen, onSend, onRename, onDuplicate, onDelete }: LayoutCardProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const menu = useLayoutMenu({ onRename, onDuplicate, onDelete })
  const tpl = getTemplate(layout.template)
  return (
    <ContentCard
      title={layout.name}
      subtitle={tpl.label}
      onOpen={onOpen}
      onPublish={can('playlists:edit') ? onSend : undefined}
      menu={menu}
      media={
        <div className="block w-full aspect-video bg-amber-500/10 overflow-hidden relative">
          {thumb ? (
            <img src={thumb} alt={layout.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full p-3">
              {/* Pinned LTR — see ZonePreviewFrame/LayoutDialog: this diagrams
                  the physical zone layout the TV actually renders, which
                  never mirrors with the dashboard's language. */}
              <div
                dir="ltr"
                className="w-full h-full bg-amber-200/40 rounded-md overflow-hidden"
                style={{
                  display: 'grid',
                  ...gridTracks(tpl, layout.column_weights ?? undefined, layout.row_weights ?? undefined),
                  gap: '4px',
                }}
              >
                {tpl.zones.map((slot, i) => (
                  <div
                    key={i}
                    style={zoneStyle(slot)}
                    className="bg-amber-500/30 flex items-center justify-center rounded-sm"
                  >
                    <span className="text-[10px] font-semibold text-amber-900/80">{i + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Badge variant="secondary" className="absolute bottom-2 start-2 h-5 text-[10px] px-1.5">
            {tpl.zones.length} {t('layout.zoneCount', 'zones')}
          </Badge>
        </div>
      }
    />
  )
}

function LayoutListRow({ layout, thumb, onOpen, onSend, onRename, onDuplicate, onDelete }: LayoutCardProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const menu = useLayoutMenu({ onRename, onDuplicate, onDelete })
  const tpl = getTemplate(layout.template)
  return (
    <ContentListRow
      title={layout.name}
      subtitle={`${tpl.label} · ${tpl.zones.length} ${t('layout.zoneCount', 'zones')}`}
      thumb={thumb}
      icon={LayoutTemplateIcon}
      tintClassName="bg-amber-500/10"
      iconClassName="text-amber-600"
      onOpen={onOpen}
      onPublish={can('playlists:edit') ? onSend : undefined}
      menu={menu}
    />
  )
}

